/**
 * frontend/src/pages/DigItem.jsx — /dig/item/:identifier
 * SoundDAO Sprint 6A — one Archive recording/release: tracks, ▶ each, "+ Playlist".
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { digItem, formatDuration, formatShowDate, itemToQueue } from '../lib/archiveApi';
import { usePlayer } from '../player/PlayerContext';
import ArtistChip, { NominateButton } from '../components/archive/ArtistChip';
import AddToPlaylistSheet from '../components/archive/AddToPlaylistSheet';
import Toast from '../components/archive/Toast';
import '../styles/archive.css';

export default function DigItem() {
  const { identifier } = useParams();
  const navigate = useNavigate();
  const { playQueue, toggle, isCurrent, status } = usePlayer();
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(null);   // track being added
  const [toast, setToast] = useState(null);

  const load = useCallback(() => {
    setError(null);
    setItem(null);
    digItem(identifier)
      .then((d) => setItem(d.item))
      .catch((e) => setError(e));
  }, [identifier]);

  useEffect(() => { load(); }, [load]);

  const queue = item ? itemToQueue(item) : [];
  const playAt = (i) => {
    if (isCurrent(queue[i].key)) toggle();
    else playQueue(queue, i);
  };
  const clearToast = useCallback(() => setToast(null), []);

  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate('/dig'));

  return (
    <div className="ax-page">
      <div className="ax-wrap">
        <button type="button" className="ax-back" onClick={goBack}>‹ Dig</button>

        {error && (
          <div className={`ax-banner ${error.code === 'ARCHIVE_UNAVAILABLE' ? '' : 'ax-banner--error'}`}>
            <span>{error.status === 404 ? 'This recording isn’t available on SoundDAO (missing, or its license doesn’t allow it).' : error.message}</span>
            {error.status !== 404 && <button type="button" className="ax-btn ax-btn--sm" onClick={load}>Retry</button>}
          </div>
        )}
        {!item && !error && <><div className="ax-skel" style={{ height: 120 }} /><div className="ax-skel" /><div className="ax-skel" /></>}

        {item && (
          <>
            <div className="ax-row" style={{ alignItems: 'flex-start', gap: 14 }}>
              <div className="ax-cover ax-cover--lg" aria-hidden="true">{item.source === 'netlabels' ? '💿' : '🎸'}</div>
              <div className="ax-grow">
                <h1 className="ax-h1" style={{ fontSize: 19 }}>{item.title}</h1>
                <div className="ax-meta">
                  {[formatShowDate(item.date), item.venue, item.coverage, `${item.tracks.length} track${item.tracks.length === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}
                </div>
                {item.artists.length > 0 && <div className="ax-chips">{item.artists.map((a) => <ArtistChip key={a.id} artist={a} />)}</div>}
              </div>
            </div>

            <div className="ax-actions">
              <button type="button" className="ax-btn ax-btn--primary" onClick={() => queue.length && playQueue(queue, 0)} disabled={!queue.length}>▶ Play all</button>
              <NominateButton artists={item.artists} />
            </div>
            <p className="ax-attr">
              Streaming from the Internet Archive · <a href={item.archiveUrl} target="_blank" rel="noopener noreferrer">{item.archiveUrl.replace('https://', '')} ↗</a>
              {item.license?.streamOnly ? ' · stream only' : item.license?.licenseUrl ? <> · <a href={item.license.licenseUrl} target="_blank" rel="noopener noreferrer">license ↗</a></> : ''}
            </p>

            <h2 className="ax-h2">Tracks</h2>
            {item.tracks.length === 0 && <div className="ax-empty">No MP3 tracks in this item.</div>}
            {item.tracks.map((t, i) => {
              const on = isCurrent(queue[i].key);
              const playing = on && (status === 'playing' || status === 'loading');
              return (
                <div className="ax-track" key={t.fileName}>
                  <span className="ax-track-n">{t.track || i + 1}</span>
                  <button type="button" className={`ax-play ${on ? 'on' : ''}`} onClick={() => playAt(i)} aria-label={playing ? `Pause ${t.title}` : `Play ${t.title}`}>
                    {playing ? '❚❚' : '▶'}
                  </button>
                  <div className="ax-grow"><div className="ax-title">{t.title}</div></div>
                  <span className="ax-dur">{formatDuration(t.durationSec)}</span>
                  <button type="button" className="ax-plus" onClick={() => setAdding(t)}>+ Playlist</button>
                </div>
              );
            })}
          </>
        )}
      </div>

      {adding && item && (
        <AddToPlaylistSheet
          track={adding}
          identifier={item.identifier}
          itemSource={item.source}
          artistTag={item.artists[0]?.tag || 'none'}
          onClose={() => setAdding(null)}
          onAdded={(p) => { setAdding(null); setToast({ type: 'info', text: `Added to “${p.title}”` }); }}
        />
      )}
      <Toast toast={toast} onDone={clearToast} />
    </div>
  );
}
